import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Loader2, Mail, CheckCircle2, UserPlus, Phone, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api, { formatApiError } from '../api';
import { CLIENT_TYPES } from '../constants';

const emptyClient = { name: '', type: 'particulier', phone: '', email: '', address: '', city: '', postalCode: '', comment: '' };

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyClient);
  const [saving, setSaving] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const { data } = await api.get(`/clients?${params.toString()}`);
      setClients(data);
    } catch (e) {
      toast.error('Erreur de chargement des clients');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const createClient = async () => {
    if (!form.name || form.name.length < 2) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      await api.post('/clients', payload);
      toast.success('Client créé');
      setShowCreate(false);
      setForm(emptyClient);
      fetchClients();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendAccess = async (client, e) => {
    e.preventDefault();
    if (!client.email) { toast.error('Ce client n\'a pas d\'email'); return; }
    try {
      const { data } = await api.post(`/clients/${client.id}/access`);
      toast.success(data.emailSent ? 'Accès envoyé par email' : 'Compte créé (email non envoyé)');
      fetchClients();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-clients">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Clients</h1>
          <p className="text-neutral-500">{clients.length} client(s)</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-red-700 hover:bg-red-600" data-testid="create-client-button">
          <Plus className="w-4 h-4 mr-2" /> Nouveau client
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input placeholder="Rechercher par nom, email, téléphone, ville..." value={search}
          onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white" data-testid="client-search-input" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-red-700" /></div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center text-neutral-400">Aucun client trouvé.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <div key={c.id} className="bg-white rounded-xl border border-neutral-200 p-4 flex flex-col" data-testid={`client-card-${c.id}`}>
              <Link to={`/admin/clients/${c.id}`} className="flex-1">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-bold text-neutral-900 truncate">{c.name}</p>
                    <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">{CLIENT_TYPES[c.type]}</span>
                  </div>
                  {c.hasAccess && <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" title="Accès actif" />}
                </div>
                <div className="mt-3 space-y-1 text-sm text-neutral-600">
                  {c.phone && <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {c.phone}</p>}
                  {c.email && <p className="flex items-center gap-1.5 truncate"><Mail className="w-3.5 h-3.5" /> {c.email}</p>}
                  {c.city && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {c.city} {c.postalCode}</p>}
                </div>
              </Link>
              <div className="mt-4 pt-3 border-t border-neutral-100">
                <Button variant="outline" size="sm" className="w-full" onClick={(e) => sendAccess(c, e)}
                  disabled={!c.email} data-testid={`send-access-${c.id}`}>
                  <UserPlus className="w-4 h-4 mr-2" /> {c.hasAccess ? 'Renvoyer l\'accès' : 'Envoyer l\'accès'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog création */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouveau client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom / Société *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="client-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CLIENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-form-phone" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-form-email" />
            </div>
            <div>
              <Label>Adresse</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-form-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ville</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="client-form-city" />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} data-testid="client-form-postal" />
              </div>
            </div>
            <div>
              <Label>Commentaire</Label>
              <Textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={2} data-testid="client-form-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={createClient} disabled={saving} className="bg-red-700 hover:bg-red-600" data-testid="client-form-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Créer le client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClients;
